import React, { useState, useEffect, useRef } from 'react';

import { Button, Checkbox, FormControlLabel, Grid, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import FormDialog from './FormDialog';
import { useFormik } from 'formik';
import { callUploadAttendeesToHS } from 'redux/actions/staffingActions';
import { enqueueSuccessSnackbar, enqueueErrorSnackbar } from 'redux/actions/notifierActions';
import { showAppOverlaySpinner } from 'redux/actions/facilitatorSessionActions';
import { useDispatch } from 'react-redux';
import { useConfirm } from 'material-ui-confirm';
import { useInternalApiClient } from 'hooks/useInternalApiClient';

const useStyles = makeStyles(theme => ({
  uploadInstructions: {
    marginBottom: theme.spacing(2),
    fontSize: '14px',
    color: theme.palette.text.secondary,
  },
  attendeesFolderLink: {
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  formField: {
    marginRight: theme.spacing(2),
  },
  checkboxField: {
    marginTop: theme.spacing(2),
  },
  formButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
    '& > button': {
      marginLeft: theme.spacing(2),
    },
  },
  cancelButton: {
    width: 105,
    height: 40,
    marginRight: 'auto',
    background: '#9E9E9E',
    color: '#fff',
    fontSize: 14,
    '&:hover': {
      background: '#9E9E9E',
      opacity: 0.9,
    },
  },
  uploadLogsContainer: {
    height: '300px',
    overflowY: 'auto',
    marginBottom: theme.spacing(2),
    border: '1px solid #e0e0e0',
    padding: theme.spacing(1),
    '& p': {
      marginBottom: '0.25em',
      lineHeight: 1.2,
    },
  },
}));

const UPLOAD_POLL_INTERVAL_MS = 2000;
/** After this many consecutive poll failures, surface a snackbar (interval keeps running). */
const UPLOAD_POLL_FAILURE_SNACKBAR_THRESHOLD = 5;

export default function UploadAttendeesDialog({ isOpen, handleClose, project }) {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const apiClient = useInternalApiClient();

  const [autoScroll, setAutoScroll] = useState(true);
  const [showUploadForm, setShowUploadForm] = useState(true);
  const [uploadLogs, setUploadLogs] = useState([]);
  const [isUploadCompleted, setIsUploadCompleted] = useState(false);
  const [uploadSessionId, setUploadSessionId] = useState(null);

  const logsEndRef = useRef(null);
  /** Counts consecutive poll failures so we can warn without aborting the interval. */
  const consecutivePollFailuresRef = useRef(0);
  const projectId = project.id;

  const formik = useFormik({
    initialValues: {
      skipRows: 0,
      maxRows: 100,
      dryRun: true,
    },
    onSubmit: (values, { setSubmitting }) => {
      setUploadLogs([]);
      setIsUploadCompleted(false);
      setShowUploadForm(false);
      consecutivePollFailuresRef.current = 0;

      dispatch(
        callUploadAttendeesToHS(
          projectId,
          values.dryRun,
          values.skipRows,
          values.maxRows,
          sessionId => {
            setUploadSessionId(sessionId);
            dispatch(showAppOverlaySpinner(false));
            dispatch(enqueueSuccessSnackbar());
            setSubmitting(false);
          },
          error => {
            console.error('Upload failed:', error);
            dispatch(showAppOverlaySpinner(false));
            dispatch(enqueueErrorSnackbar('Upload failed'));
            setSubmitting(false);
            setShowUploadForm(true);
          },
        ),
      );
    },
  });

  useEffect(() => {
    if (!uploadSessionId) {
      return undefined;
    }

    let isCancelled = false;
    consecutivePollFailuresRef.current = 0;

    const pollUploadProgress = async () => {
      try {
        const data = await apiClient.hubspot.attendeeUploadSessions.get(uploadSessionId);

        if (isCancelled) {
          return;
        }

        consecutivePollFailuresRef.current = 0;

        if (data?.logs?.length) {
          const logMessages = data.logs.map(log => log.message);
          setUploadLogs(logMessages);
        }

        if (data.completed || data.aborted) {
          setIsUploadCompleted(true);
        }
      } catch (error) {
        console.error('Error polling upload progress:', error);

        if (isCancelled) {
          return;
        }

        consecutivePollFailuresRef.current += 1;

        // Non-blocking: keep polling, but tell the admin something is wrong after several failures.
        if (consecutivePollFailuresRef.current === UPLOAD_POLL_FAILURE_SNACKBAR_THRESHOLD) {
          dispatch(
            enqueueErrorSnackbar(
              'Still waiting for upload progress… Check your connection or try aborting and uploading again.',
            ),
          );
        }
      }
    };

    pollUploadProgress();
    const intervalId = setInterval(pollUploadProgress, UPLOAD_POLL_INTERVAL_MS);

    return () => {
      isCancelled = true;
      clearInterval(intervalId);
    };
  }, [uploadSessionId, apiClient, dispatch]);

  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [autoScroll, uploadLogs]);

  const classes = useStyles();

  const handleDialogClose = () => {
    if (!isUploadCompleted && uploadSessionId) {
      abortAndCloseUploadDialog();
    } else {
      handleClose();
      resetUploadForm();
    }
  };

  const abortUpload = async () => {
    if (!isUploadCompleted && uploadSessionId) {
      try {
        await apiClient.hubspot.attendeeUploadSessions.abort(uploadSessionId);
        dispatch(enqueueSuccessSnackbar('Upload aborted successfully'));
      } catch (error) {
        console.error('Error aborting upload:', error);
        dispatch(enqueueErrorSnackbar('Failed to abort upload'));
      }
    }
  };

  const abortAndCloseUploadDialog = async () => {
    if (!isUploadCompleted && uploadSessionId) {
      confirm({
        description: 'Are you sure you want to abort the upload?',
      })
        .then(async () => {
          await abortUpload();
          resetUploadForm();
          handleClose();
        })
        .catch(() => {
          // User cancelled the abort action
        });
    } else {
      resetUploadForm();
      handleClose();
    }
  };

  const resetUploadForm = () => {
    formik.resetForm();
    setUploadSessionId(null);
    setUploadLogs([]);
    setShowUploadForm(true);
    setIsUploadCompleted(false);
  };

  return (
    <FormDialog
      isOpen={isOpen}
      handleClose={handleDialogClose}
      isTitleHasCloseIcon={true}
      title="Upload Attendees to HubSpot"
    >
      {showUploadForm ? (
        <form onSubmit={formik.handleSubmit}>
          <div className={classes.uploadInstructions}>
            <p>
              Upload the file from WordPress to the{' '}
              {project.attendeesFolder?.webViewLink ? (
                <a
                  href={project.attendeesFolder.webViewLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.attendeesFolderLink}
                >
                  attendees folder
                </a>
              ) : (
                'attendees folder'
              )}{' '}
              and name it <strong>attendee-hubspot-upload.csv</strong> before proceeding. Switch to
              json upload.
            </p>
          </div>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4}>
              <TextField
                fullWidth
                id="skipRows"
                name="skipRows"
                label="Skip Rows"
                type="number"
                value={formik.values.skipRows}
                onChange={formik.handleChange}
                error={formik.touched.skipRows && Boolean(formik.errors.skipRows)}
                helperText={formik.touched.skipRows && formik.errors.skipRows}
                margin="normal"
                className={classes.formField}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                id="maxRows"
                name="maxRows"
                label="Max Rows"
                type="number"
                value={formik.values.maxRows}
                onChange={formik.handleChange}
                error={formik.touched.maxRows && Boolean(formik.errors.maxRows)}
                helperText={formik.touched.maxRows && formik.errors.maxRows}
                margin="normal"
                className={classes.formField}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    id="dryRun"
                    name="dryRun"
                    checked={formik.values.dryRun}
                    onChange={formik.handleChange}
                    color="primary"
                  />
                }
                label="Dry Run"
                className={classes.checkboxField}
              />
            </Grid>
          </Grid>

          <div className={classes.formButtonContainer}>
            <Button
              className={classes.cancelButton}
              variant="contained"
              onClick={handleClose}
              disabled={formik.isSubmitting}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              disabled={formik.isSubmitting}
            >
              Submit
            </Button>
          </div>
        </form>
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4>Upload Progress</h4>
            <FormControlLabel
              control={
                <Checkbox
                  checked={autoScroll}
                  onChange={e => setAutoScroll(e.target.checked)}
                  color="primary"
                />
              }
              label="Auto-scroll to new logs"
            />
          </div>
          <p>Session ID: {uploadSessionId}</p>
          <div className={classes.uploadLogsContainer}>
            {uploadLogs.length > 0 ? (
              uploadLogs.map((log, index) => (
                <p key={index} style={{ fontWeight: log.startsWith('***') ? 'bold' : 'normal' }}>
                  {log}
                </p>
              ))
            ) : (
              <p>Waiting for logs...</p>
            )}
            <div ref={logsEndRef} />
          </div>
          <div className={classes.formButtonContainer}>
            <Button
              className={classes.cancelButton}
              variant="contained"
              onClick={abortAndCloseUploadDialog}
            >
              {isUploadCompleted ? 'Close' : 'Abort'}
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={resetUploadForm}
              disabled={!isUploadCompleted}
            >
              Upload Again
            </Button>
          </div>
        </div>
      )}
    </FormDialog>
  );
}
